import { create } from 'zustand';
import { authApi, type LoginResponse, type UserProfile } from '../api/auth';

interface AuthState {
  user: UserProfile | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  login: (email: string, password: string) => Promise<void>;
  googleLogin: (idToken: string) => Promise<void>;
  register: (name: string, email: string, password: string) => Promise<void>;
  logout: () => void;
  fetchProfile: () => Promise<void>;
  updateProfile: (data: Partial<Pick<UserProfile, 'name' | 'default_instrument'>>) => Promise<void>;
  uploadAvatar: (file: File) => Promise<void>;
  selectPresetAvatar: (presetName: string) => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isAuthenticated: !!localStorage.getItem('access_token'),
  isLoading: false,
  error: null,

  login: async (email: string, password: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await authApi.login({ email, password });
      const { access, refresh, user } = response.data;
      localStorage.setItem('access_token', access);
      localStorage.setItem('refresh_token', refresh);
      set({ user, isAuthenticated: true, isLoading: false });
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ||
        'Login failed';
      set({ error: message, isLoading: false });
      throw err;
    }
  },

  googleLogin: async (idToken: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await authApi.googleLogin(idToken);
      const { access, refresh, user } = response.data;
      localStorage.setItem('access_token', access);
      localStorage.setItem('refresh_token', refresh);
      set({ user, isAuthenticated: true, isLoading: false });
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ||
        'Falha ao entrar com Google';
      set({ error: message, isLoading: false });
      throw err;
    }
  },

  register: async (name: string, email: string, password: string) => {
    set({ isLoading: true, error: null });
    try {
      await authApi.register({ name, email, password });
      set({ isLoading: false });
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ||
        'Registration failed';
      set({ error: message, isLoading: false });
      throw err;
    }
  },

  logout: () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    set({ user: null, isAuthenticated: false, error: null });
  },

  fetchProfile: async () => {
    try {
      const response = await authApi.getProfile();
      set({ user: response.data, isAuthenticated: true });
    } catch {
      set({ user: null, isAuthenticated: false });
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
    }
  },

  updateProfile: async (data) => {
    const response = await authApi.updateProfile(data);
    set((state) => ({ user: state.user ? { ...state.user, ...response.data } : response.data }));
  },

  uploadAvatar: async (file: File) => {
    const response = await authApi.uploadAvatar(file);
    set((state) => ({
      user: state.user ? { ...state.user, avatar_url: response.data.avatar_url, avatar_type: response.data.avatar_type } : state.user,
    }));
  },

  selectPresetAvatar: async (presetName: string) => {
    const response = await authApi.selectPresetAvatar(presetName);
    set((state) => ({
      user: state.user ? { ...state.user, avatar_url: response.data.avatar_url, avatar_type: response.data.avatar_type } : state.user,
    }));
  },

  clearError: () => set({ error: null }),
}));
